import { Bus, ShoppingCart, Waves, Users, Route, ShoppingBasket, Sparkles } from "lucide-react";
import PageHero, { NextPage } from "@/components/PageHero";
import { FadeUp, StaggerGroup, StaggerItem } from "@/components/Reveal";

const PARK_IMG =
  "https://customer-assets-lqy194kg.emergentagent.net/job_page-to-site-4/artifacts/f6aa575a6381f5e6_play.webp";

const RESOURCES = [
  {
    title: "Transport",
    Icon: Bus,
    color: "#696DFF",
    copy: "Aylesbury railway station and Aylesbury Vale Parkway provide Chiltern Railways services, including journeys towards London Marylebone. Aylesbury Bus Station and local bus services connect the town and surrounding communities. Staff will check current timetables and disruptions before each journey.",
    testid: "transport-card",
  },
  {
    title: "Shopping",
    Icon: ShoppingCart,
    color: "#FFB019",
    copy: "Tesco Extra at Broadfields Retail Park is also on Bicester Road (HP19 8BU). Other nearby choices include Sainsbury's at Gatehouse Road (HP19 8ED), Tesco Express on Jackson Road (HP19 9BF), and the shops and services at Friars Square and Aylesbury town centre.",
    testid: "shopping-card",
  },
  {
    title: "Leisure",
    Icon: Waves,
    color: "#5CFFAF",
    copy: "Vale Park and Aqua Vale Swimming and Fitness Centre offer swimming, gym, football, basketball and outdoor fitness opportunities. Aylesbury Library on Walton Street provides study resources, digital access and a recognised Safe Place for anyone who feels unsafe while out in the community.",
    testid: "leisure-card",
  },
];

const SAFETY_BULLETS = [
  {
    Icon: Route,
    copy: "Travel training and route planning will be introduced at a pace consistent with the child's age, confidence and legal arrangements.",
  },
  {
    Icon: ShoppingBasket,
    copy: "Shopping will be used to develop budgeting, meal planning, decision-making and respectful community participation.",
  },
  {
    Icon: Sparkles,
    copy: "Leisure choices will balance physical activity, creativity, social opportunity, rest and the child's own preferences.",
  },
];

export default function LocalArea() {
  return (
    <div data-testid="page-local-area">
      <PageHero
        testid="local-hero"
        eyebrow="Local area & community"
        title={[
          "A connected",
          <>
            <em className="text-rainbow italic">local network</em>
          </>,
        ]}
        lead="The home is on the Bicester Road corridor in north-west Aylesbury, with access to the town centre, everyday shopping, public transport, green space and community services. Staff will use these resources to support ordinary childhood experiences and increasing independence, subject to the child's care plan, court order and current risk assessment."
      />

      {/* Resources */}
      <section className="container-x" data-testid="resources-section">
        <StaggerGroup className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {RESOURCES.map(({ title, Icon, color, copy, testid }) => (
            <StaggerItem key={title}>
              <div className="card h-full p-8 transition-[transform,box-shadow] duration-200 hover:-translate-y-1 hover:shadow-[0_24px_45px_-28px_rgba(45,55,72,0.45)]" data-testid={testid}>
                <div className="flex items-center justify-between">
                  <span className="flex h-12 w-12 items-center justify-center rounded-full bg-sand text-ink">
                    <Icon className="h-6 w-6" strokeWidth={1.5} />
                  </span>
                  <span className="dot" style={{ background: color }} aria-hidden="true" />
                </div>
                <h3 className="mt-6 font-display text-2xl text-ink">{title}</h3>
                <p className="mt-3 text-base leading-relaxed text-mute">{copy}</p>
              </div>
            </StaggerItem>
          ))}
        </StaggerGroup>
      </section>

      {/* Youth support */}
      <section className="container-x py-16 sm:py-20 lg:py-24" data-testid="youth-section">
        <FadeUp>
          <div className="card edge-rainbow p-6 sm:p-8" data-testid="youth-card">
            <div className="flex items-start gap-6">
              <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-sand text-ink">
                <Users className="h-7 w-7" strokeWidth={1.5} />
              </span>
              <div>
                <p className="text-xs font-extrabold uppercase tracking-[0.18em] text-mute">Youth support</p>
                <p className="mt-4 max-w-4xl text-lg leading-relaxed text-ink/90" data-testid="youth-copy">
                  Elmhurst Best Start Family Hub on Dunsham Lane provides free youth and family
                  activities, SEND guidance and a Senior Hangout for young people aged 12 and over.
                  Other local youth, sports and community activities will be selected around the
                  child's interests and assessed compatibility.
                </p>
              </div>
            </div>
          </div>
        </FadeUp>
      </section>

      {/* Using the community safely */}
      <section className="container-x" data-testid="safety-section">
        <div className="grid items-center gap-10 lg:grid-cols-12">
          <FadeUp className="lg:col-span-6">
            <div className="overflow-hidden rounded-[2rem] border border-line shadow-[0_30px_60px_-35px_rgba(45,55,72,0.4)]">
              <img
                src={PARK_IMG}
                alt="A modern UK playground at dusk with a lit timber canopy and slide"
                className="h-[300px] w-full object-cover sm:h-[420px]"
                data-testid="community-image"
              />
            </div>
          </FadeUp>
          <div className="lg:col-span-6">
            <FadeUp>
              <p className="eyebrow">Using the community safely</p>
              <h2 className="mt-4 font-display text-3xl tracking-tight text-ink sm:text-4xl" data-testid="safety-title">
                Not a generic timetable
              </h2>
              <p className="mt-6 text-lg leading-relaxed text-mute" data-testid="safety-copy">
                Community access will not be treated as a generic timetable. Key workers will help
                the child choose meaningful activities, prepare for journeys and review what went
                well. Plans will identify staffing, transport, spending, communication, online
                contact, missing-from-care risks and any court-authorised restrictions. The least
                restrictive safe option will always be considered.
              </p>
            </FadeUp>
            <StaggerGroup className="mt-8 space-y-4">
              {SAFETY_BULLETS.map(({ Icon, copy }, i) => (
                <StaggerItem key={i}>
                  <div className="flex items-start gap-4 rounded-2xl border border-line bg-white p-5" data-testid={`safety-bullet-${i + 1}`}>
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-sand text-ink">
                      <Icon className="h-5 w-5" strokeWidth={1.5} />
                    </span>
                    <p className="text-base leading-relaxed text-ink/90">{copy}</p>
                  </div>
                </StaggerItem>
              ))}
            </StaggerGroup>
          </div>
        </div>
      </section>

      <div className="container-x pb-4 pt-16 sm:pt-20">
        <NextPage
          to="/education-outcomes"
          title="Education, health and positive outcomes"
          label="Local provision, coordination and what we work towards"
          testid="next-education"
        />
      </div>
    </div>
  );
}
